import {inject, Injectable} from '@angular/core';
import {CanActivateFn, Router} from '@angular/router';
import {AuthService} from '../../../services/auth.service';
import {UserWebservice} from '../../../services/webservices/user.webservice';
import {LogoutService} from '../../../services/logout.service';
import {HeaderTextService} from '../../../services/header-text.service';
import {take} from 'rxjs/operators';
import {User} from 'src/app/model/user/user.model';
import {ApplicationInsightsService} from 'src/app/services/application-insights.service';
import {CheckModule} from '../check-module/check-module.component';
import {MsalService} from "@azure/msal-angular";
import {environment} from "../../../../environments/environment";

export const authGuard: CanActivateFn = async (route, _state) => {
  const authGuardService = inject(AuthGuardService);
  const userWebservice = inject(UserWebservice);

  if (!userWebservice.currentUser) {
    return await authGuardService.refreshCurrentUser(route.routeConfig.path);
  } else {
    return true;
  }
};

@Injectable({
    providedIn: 'root'
})
export class AuthGuardService  {
    private readonly router = inject(Router);
    private readonly authService = inject(AuthService);
    private readonly msalService = inject(MsalService);
    private readonly userService = inject(UserWebservice);
    private readonly logoutService = inject(LogoutService);
    private readonly applicationInsightsService = inject(ApplicationInsightsService);
    private readonly checkModule = inject(CheckModule);
    private readonly headerTextService = inject(HeaderTextService);

    token: string;

  refreshCurrentUser(path: string): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.userService.currentUser) {
        this.userService.refreshCurrentUser().pipe(take(1))
          .subscribe({
            next: (data: User) => {
              this.userService.currentUser = data;
              this.userService.currentUserModule = data.listOfModule;
              this.checkAccess(path, '/children');
              resolve(true);
            },
            error: () => {
              this.userService.currentUser = new User();
              this.userService.currentUser.id = -1;
              resolve(false);
            }
          });
      }
      resolve(true);
    });
  }

    checkUserCanGo(path: string) {
        this.token = localStorage.getItem('token');
        if (this.token) { // Pas de token --> doit se reco
            if (this.authService.token !== this.token) { // le token s'est mis à jour
                this.refreshLocalToken(this.token);
            }

            this.authService.isAuth = true;

            this.headerTextService.showLogoutBtn.next(true);

            if (!this.userService.currentUser) { // Pas d'utilisateur stocké en ce moment dans le userService
                this.userService.refreshCurrentUser().pipe(take(1))
                    .subscribe({
                        next: (data: User) => {
                            this.userService.currentUser = data;
                            this.userService.currentUserModule = data.listOfModule;
                            this.applicationInsightsService.setUserId(this.userService.currentUser.id.toString());

                            this.checkAccess(path, '/children');
                        },
                        error: () => {
                            this.userService.currentUser = new User();
                            this.userService.currentUser.id = -1;
                        }
                    });
            } else {
                this.checkAccess(path, '/children');
            }
        } else { // No token in local storage
            this.disconnectUser();
        }

      return false;
    }

    checkUserCanGoOnMobile(): Promise<boolean> {
        this.token = localStorage.getItem('token');

        // Check si a un token dans le local storage
        //    Si oui vérifie que le token est toujours valide
        //      Si non le rafraichi
        //      Dans tous les cas rafraichi l'objet user de userService
        //      Valide le rôle de l'user
        //
        // Si non déco

        if (this.token) { // Pas de token --> doit se reco
            return new Promise<boolean>((resolve, reject) => {
                if (localStorage.getItem('token') !== this.token) { // le token s'est mis à jour
                    this.refreshLocalToken(this.token);
                }

                this.authService.isAuth = true;

                if (!this.userService.currentUser) { // Pas d'utilisateur stocké en ce moment dans le userService
                    this.userService.refreshCurrentUser().pipe(take(1))
                        .subscribe({
                            next: (data: User) => {
                                this.userService.currentUser = data;
                                this.userService.currentUserModule = data.listOfModule;
                                this.applicationInsightsService.setUserId(this.userService.currentUser.id.toString());

                                resolve(this.checkHasRightToAccess());
                            },
                            error: () => {
                                this.userService.currentUser = new User();
                                this.userService.currentUser.id = -1;
                                reject(new Error());
                            }
                        });
                } else {
                    resolve(this.checkHasRightToAccess());
                }
            })
                .catch(() => false)
                .then(value => value);

        } else { // No token in local storage
            this.disconnectUser();
            return Promise.resolve(false);
        }
    }

    logout() {
      this.msalService.logoutRedirect({
        postLogoutRedirectUri: environment.azureb2c.postLogoutRedirectUri
      });
    }

    checkHasRightToAccess() {
        this.authService.isAuthBDD = true;
        return true;
    }

    disconnectUser() {
      this.logoutService.logout();
      return Promise.resolve(false);
    }

    refreshLocalToken(newToken) {
        localStorage.setItem('token', newToken);
        this.authService.token = newToken;
    }
  private checkAccess(path: string, callback: string) {
    if (this.checkModule.canAccess(path)) {
      return this.checkHasRightToAccess();
    } else {
      return this.router.navigate([callback]);
    }
  }
}
